import { IconButton, Stack, Typography } from '@mui/material';
import { Favorite, FavoriteBorder, Rocket, RocketOutlined } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../auth/Auth';
import { useApi } from '../api/Api';
import { useRequest } from '../api/Request';
import { Like, Question } from '../database/theSource';
import { useEffect, useMemo, useState } from 'react';
import { CommunityPost } from '../database/community';
import { LikeParentType } from '../theSource/constants/LikeParentType';
import { Thread } from '../database/forum';

export interface LikeButtonProps<T> {
    parentType: LikeParentType;
    pk: string;
    sk: string;
    likes: Record<string, Like> | null;
    onEdit: (data: T) => void;
}

const LikeButton = <T extends Question | CommunityPost | Thread>({
    parentType,
    pk,
    sk,
    likes,
    onEdit,
}: LikeButtonProps<T>) => {
    const { user } = useAuth();
    const api = useApi();
    const request = useRequest();
    const [isLiked, setIsLiked] = useState<boolean>(
        likes && user ? !!likes[user.username] : false
    );

    useEffect(() => {
        setIsLiked(likes && user ? !!likes[user.username] : false);
    }, [likes, user]);

    const likers = useMemo(() => {
        return likes ? Object.keys(likes) : [];
    }, [likes]);

    const onLike = () => {
        if (!user) {
            return;
        }
        request.onStart();
        switch (parentType) {
            case LikeParentType.Question:
                api.setQuestionLike(pk, sk, { isLiked: !isLiked })
                    .then((resp) => {
                        setIsLiked(!isLiked);
                        onEdit(resp.data as T);
                        request.onSuccess();
                    })
                    .catch((err) => {
                        console.error(err);
                        request.onFailure(err);
                    });
                break;
            case LikeParentType.CommunityPost:
                api.setCommunityPostLike(pk, sk, { isLiked: !isLiked })
                    .then((resp) => {
                        setIsLiked(!isLiked);
                        onEdit(resp.data as T);
                        request.onSuccess();
                    })
                    .catch((err) => {
                        console.error(err);
                        request.onFailure(err);
                    });
                break;
            case LikeParentType.Thread:
                api.setThreadLike(pk, sk, { isLiked: !isLiked })
                    .then((resp) => {
                        setIsLiked(!isLiked);
                        onEdit(resp.data as T);
                        request.onSuccess();
                    })
                    .catch((err) => {
                        console.error(err);
                        request.onFailure(err);
                    });
        }
    };

    return (
        <Stack direction='row' alignItems='center'>
            <motion.span
                key={likers.length}
                initial={{ scale: 1.3, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                style={{ display: 'inline-block' }}
            >
                <Typography ml='0.375rem' fontWeight='600'>
                    {likers.length}
                </Typography>
            </motion.span>
            <motion.div
                whileTap={{ scale: 1.4 }}
                transition={{ type: 'spring', stiffness: 500, damping: 12 }}
            >
                <IconButton
                    onClick={() => onLike()}
                    sx={{
                        color: isLiked ? 'red' : 'gray',
                        transition: 'color 0.25s ease',
                    }}
                >
                    <AnimatePresence mode='wait'>
                        <motion.div
                            key={isLiked ? 'liked' : 'unliked'}
                            initial={{ scale: 0.4, opacity: 0, rotate: -20 }}
                            animate={{ scale: 1, opacity: 1, rotate: 0 }}
                            exit={{ scale: 0.4, opacity: 0, rotate: 20 }}
                            transition={{ duration: 0.18, ease: 'easeOut' }}
                        >
                            {parentType === LikeParentType.Thread ? (
                                isLiked ? (
                                    <Rocket sx={{ color: 'red' }} />
                                ) : (
                                    <RocketOutlined sx={{ color: 'gray' }} />
                                )
                            ) : isLiked ? (
                                <Favorite sx={{ color: 'red' }} />
                            ) : (
                                <FavoriteBorder sx={{ color: 'gray' }} />
                            )}
                        </motion.div>
                    </AnimatePresence>
                </IconButton>
            </motion.div>
        </Stack>
    );
};

export default LikeButton;
